import {
  Engine,
  WarmupController,
  chapterIndexByBlock,
  languagePack,
  tokenize,
  type Doc,
  type EngineEvent,
  type Token,
} from '@vif/core';

/**
 * Engine-as-store (P1 brief stack decision): one controller instance owns
 * the core Engine, the rAF loop, chapter overlay state and persistence
 * cadence; React subscribes via useSyncExternalStore. No DOM APIs in here —
 * the host adapter is injected, so lifecycle behavior (PD-15/16) is
 * unit-testable in Node.
 */

/** Prototype behavior: chapter overlay auto-resumes after 2.5 s. */
export const CHAPTER_OVERLAY_MS = 2500;
/** PRD §10: position saved on pause / every 100 words / lifecycle events. */
export const POSITION_SAVE_EVERY_WORDS = 100;
/** Prototype: sessions under 10 words are noise, not history. */
export const SESSION_MIN_WORDS = 10;

export const WPM_STEP = 25;
export const SPEED_PRESETS = [200, 300, 500, 700, 900] as const;

export interface ChapterInfo {
  readonly title: string;
  readonly firstTokenIdx: number;
}

export interface ReaderHost {
  /** rAF seam; returns a cancel handle. */
  requestFrame(callback: () => void): number;
  cancelFrame(handle: number): void;
  setTimer(callback: () => void, ms: number): number;
  clearTimer(handle: number): void;
  /** Screen Wake Lock, playback-only (PD-15); no-ops where unsupported. */
  acquireWakeLock(): void;
  releaseWakeLock(): void;
  now(): number;
}

export interface ReaderPersistence {
  savePosition(wordIdx: number): void;
  appendSession(row: { words: number; activeMs: number; avgWpm: number }): void;
}

export interface ReaderConfig {
  readonly doc: Doc;
  readonly wpm: number;
  readonly chunkSize: 1 | 2 | 3;
  readonly adaptiveEnabled: boolean;
  readonly warmup?: { startPct: number; durationSec: 15 | 30 | 60 } | undefined;
  readonly startTokenIdx?: number;
}

export interface OverlayState {
  readonly chapterIdx: number;
  readonly title: string;
  /** True when playback will auto-resume when the overlay ends. */
  readonly resuming: boolean;
}

export interface ReaderState {
  readonly tokenIdx: number;
  readonly tokenCount: number;
  readonly playing: boolean;
  readonly finished: boolean;
  readonly wpm: number;
  readonly chapterIdx: number;
  readonly overlay: OverlayState | null;
  readonly wordsRead: number;
  readonly activeMs: number;
  readonly totalTokens: number;
}

export class ReaderController {
  readonly tokens: readonly Token[];
  readonly chapters: readonly ChapterInfo[];

  private readonly engine: Engine;
  private readonly host: ReaderHost;
  private readonly persistence: ReaderPersistence;
  private listeners: (() => void)[] = [];
  private state: ReaderState;
  private frameHandle: number | undefined;
  private overlayTimer: number | undefined;
  private lastSavedAtWords = 0;
  private sessionSaved = false;

  constructor(config: ReaderConfig, host: ReaderHost, persistence: ReaderPersistence) {
    this.host = host;
    this.persistence = persistence;
    this.tokens = tokenize(config.doc);
    this.chapters = collectChapters(config.doc, this.tokens);
    this.engine = new Engine({
      doc: config.doc,
      tokens: this.tokens,
      clock: { now: () => host.now() },
      wpm: config.wpm,
      chunkSize: config.chunkSize,
      adaptiveEnabled: config.adaptiveEnabled,
      langPack: languagePack(config.doc.lang),
      warmup:
        config.warmup === undefined
          ? undefined
          : new WarmupController({
              startPct: config.warmup.startPct,
              durationSec: config.warmup.durationSec,
            }),
    });
    this.engine.on((event) => this.onEngineEvent(event));
    if (config.startTokenIdx !== undefined && config.startTokenIdx > 0) {
      this.engine.seek(config.startTokenIdx);
    }
    this.state = this.buildState(null);
  }

  // ── store contract ────────────────────────────────────────────────────────
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  };

  getState = (): ReaderState => this.state;

  // ── transport ────────────────────────────────────────────────────────────
  play(): void {
    if (this.state.finished) return;
    this.dismissOverlay(false);
    this.engine.play();
    this.host.acquireWakeLock();
    this.scheduleFrame();
    this.publish();
  }

  pause(): void {
    this.engine.pause();
    this.stopFrame();
    this.host.releaseWakeLock();
    this.persistPosition();
    this.publish();
  }

  toggle(): void {
    if (this.engine.snapshot().playing) this.pause();
    else this.play();
  }

  restart(): void {
    this.dismissOverlay(false);
    this.engine.seek(0);
    this.publish();
  }

  seekToken(tokenIdx: number): void {
    this.engine.seek(tokenIdx);
    this.persistPosition();
    this.publish();
  }

  /** ←: start of the current sentence, or the previous one when already there. */
  sentenceBack(): void {
    const idx = this.state.tokenIdx;
    const current = this.sentenceStart(idx);
    this.seekToken(idx > current ? current : this.sentenceStart(Math.max(0, current - 1)));
  }

  sentenceForward(): void {
    const target = this.tokens.findIndex(
      (token, i) =>
        i > this.state.tokenIdx &&
        token.sentenceIdx !== this.tokens[this.state.tokenIdx]?.sentenceIdx,
    );
    if (target !== -1) this.seekToken(target);
  }

  paragraphBack(): void {
    const idx = this.state.tokenIdx;
    const current = this.blockStart(idx);
    this.seekToken(idx > current ? current : this.blockStart(Math.max(0, current - 1)));
  }

  paragraphForward(): void {
    const currentBlock = this.tokens[this.state.tokenIdx]?.blockIdx;
    const target = this.tokens.findIndex(
      (token, i) => i > this.state.tokenIdx && token.blockIdx !== currentBlock,
    );
    if (target !== -1) this.seekToken(target);
  }

  adjustWpm(delta: number): void {
    this.engine.setWpm(this.engine.getWpm() + delta);
    this.publish();
  }

  setWpm(wpm: number): void {
    this.engine.setWpm(wpm);
    this.publish();
  }

  setChunkSize(size: 1 | 2 | 3): void {
    this.engine.setChunkSize(size);
    this.publish();
  }

  /**
   * H2: UI surfaces (quick settings) suspend playback while open. Pauses
   * through the existing pause path if playing; the returned restore
   * resumes through the existing play path (warm-up resume rules included)
   * — or does nothing if playback was already paused or has finished.
   */
  suspendForUi(): () => void {
    const wasPlaying = this.engine.snapshot().playing;
    if (wasPlaying) this.pause();
    return () => {
      if (wasPlaying && !this.state.finished) this.play();
    };
  }

  // ── lifecycle (PD-7 / PD-15) ─────────────────────────────────────────────
  /** visibilitychange → hidden: auto-pause + persist (PD-15). */
  handleHidden(): void {
    if (this.engine.snapshot().playing) this.pause();
    else this.persistPosition();
  }

  /** pagehide / unmount: persist snapshot + close the session (PD-7). */
  handlePageHide(): void {
    if (this.engine.snapshot().playing) this.pause();
    this.persistPosition();
    this.closeSession();
  }

  dispose(): void {
    this.handlePageHide();
    this.stopFrame();
    this.dismissOverlay(false);
    this.host.releaseWakeLock();
  }

  // ── internals ────────────────────────────────────────────────────────────
  private onEngineEvent(event: EngineEvent): void {
    switch (event.type) {
      case 'wordChanged': {
        this.entryTokenCount = event.tokenCount;
        const snapshot = this.engine.snapshot();
        if (snapshot.wordsRead - this.lastSavedAtWords >= POSITION_SAVE_EVERY_WORDS) {
          this.persistPosition();
        }
        this.publish();
        break;
      }
      case 'chapterEntered': {
        // PD-16: the overlay fires on FORWARD transitions only; rewinding
        // across a boundary must never celebrate a chapter backwards.
        if (event.direction === 'forward') this.showOverlay(event.chapterIdx);
        break;
      }
      case 'finished': {
        this.stopFrame();
        this.host.releaseWakeLock();
        this.persistPosition();
        this.closeSession();
        this.publish();
        break;
      }
      case 'milestone':
        // Consumers are parked (soul.md §6); the event exists for them.
        break;
    }
  }

  private showOverlay(chapterIdx: number): void {
    // The overlay is a playback ritual (pause 2.5 s, announce, resume —
    // PRD §8.3). A paused seek across a boundary is just navigation.
    if (!this.engine.snapshot().playing) return;
    this.engine.pause();
    this.stopFrame();
    if (this.overlayTimer !== undefined) this.host.clearTimer(this.overlayTimer);
    const title = this.chapters[chapterIdx]?.title ?? '';
    this.publish({ chapterIdx, title, resuming: true });
    this.overlayTimer = this.host.setTimer(() => {
      this.overlayTimer = undefined;
      this.dismissOverlay(true);
    }, CHAPTER_OVERLAY_MS);
  }

  private dismissOverlay(resume: boolean): void {
    if (this.overlayTimer !== undefined) {
      this.host.clearTimer(this.overlayTimer);
      this.overlayTimer = undefined;
    }
    if (this.state.overlay === null) return;
    const shouldResume = resume && this.state.overlay.resuming && !this.state.finished;
    this.publish(null);
    if (shouldResume) {
      this.engine.play();
      this.scheduleFrame();
      this.publish();
    }
  }

  private scheduleFrame(): void {
    if (this.frameHandle !== undefined) return;
    const loop = (): void => {
      this.engine.tick();
      if (this.engine.snapshot().playing) {
        this.frameHandle = this.host.requestFrame(loop);
      } else {
        this.frameHandle = undefined;
      }
    };
    this.frameHandle = this.host.requestFrame(loop);
  }

  private stopFrame(): void {
    if (this.frameHandle !== undefined) {
      this.host.cancelFrame(this.frameHandle);
      this.frameHandle = undefined;
    }
  }

  private persistPosition(): void {
    const snapshot = this.engine.snapshot();
    this.lastSavedAtWords = snapshot.wordsRead;
    this.persistence.savePosition(snapshot.tokenIdx);
  }

  private closeSession(): void {
    if (this.sessionSaved) return;
    const snapshot = this.engine.snapshot();
    if (snapshot.wordsRead < SESSION_MIN_WORDS) return;
    this.sessionSaved = true;
    const minutes = snapshot.activeMs / 60000;
    this.persistence.appendSession({
      words: snapshot.wordsRead,
      activeMs: Math.round(snapshot.activeMs),
      avgWpm: minutes > 0 ? Math.round(snapshot.wordsRead / minutes) : 0,
    });
  }

  private sentenceStart(idx: number): number {
    const sentence = this.tokens[idx]?.sentenceIdx;
    let start = idx;
    while (start > 0 && this.tokens[start - 1]?.sentenceIdx === sentence) start--;
    return start;
  }

  private blockStart(idx: number): number {
    const block = this.tokens[idx]?.blockIdx;
    let start = idx;
    while (start > 0 && this.tokens[start - 1]?.blockIdx === block) start--;
    return start;
  }

  /** Chunk width of the current entry, tracked from wordChanged events. */
  private entryTokenCount = 1;

  private buildState(overlay: OverlayState | null): ReaderState {
    const snapshot = this.engine.snapshot();
    return {
      tokenIdx: snapshot.tokenIdx,
      tokenCount: this.entryTokenCount,
      playing: snapshot.playing,
      finished: snapshot.finished,
      wpm: this.engine.getWpm(),
      chapterIdx: snapshot.chapterIdx,
      overlay,
      wordsRead: snapshot.wordsRead,
      activeMs: snapshot.activeMs,
      totalTokens: this.tokens.length,
    };
  }

  private publish(overlay: OverlayState | null = this.state?.overlay ?? null): void {
    this.state = this.buildState(overlay);
    for (const listener of [...this.listeners]) listener();
  }
}

function collectChapters(doc: Doc, tokens: readonly Token[]): ChapterInfo[] {
  const chapterByBlock = chapterIndexByBlock(doc.blocks);
  const chapters: { title: string; firstTokenIdx: number }[] = [];
  let seen = -1;
  for (const [i, token] of tokens.entries()) {
    const chapter = chapterByBlock[token.blockIdx] ?? 0;
    if (chapter > seen) {
      seen = chapter;
      const block = doc.blocks[token.blockIdx];
      chapters.push({
        title: block?.kind === 'chapter-heading' ? (block.title ?? block.text) : '',
        firstTokenIdx: i,
      });
    }
  }
  if (chapters.length === 0) chapters.push({ title: '', firstTokenIdx: 0 });
  return chapters;
}
